import { useState, useEffect } from 'react';
import { formatDocument } from '@/lib/validations';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Smartphone, Shield, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { DriverWithAccess } from '@/hooks/use-driver-access';

interface DriverAccessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  driver: DriverWithAccess | null;
  onSave: (driverId: string, pinCode: string) => void;
  onToggle?: (driverId: string, isActive: boolean) => void;
  onReset?: (driverId: string, newPin: string) => void;
  isLoading?: boolean;
}

export function DriverAccessModal({
  open,
  onOpenChange,
  driver,
  onSave,
  onToggle,
  onReset,
  isLoading = false
}: DriverAccessModalProps) {
  const [pinCode, setPinCode] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [mode, setMode] = useState<'create' | 'edit' | 'reset'>('create');

  useEffect(() => {
    if (driver?.has_access) {
      setMode('edit');
    } else {
      setMode('create');
    }
    setPinCode('');
    setConfirmPin('');
  }, [driver, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!driver) return;

    if (mode === 'create' || mode === 'reset') {
      if (pinCode !== confirmPin) {
        alert('Os PINs não correspondem');
        return;
      }

      if (pinCode.length < 4 || pinCode.length > 6) {
        alert('O PIN deve ter entre 4 e 6 dígitos');
        return;
      }

      if (mode === 'create') {
        onSave(driver.id, pinCode);
      } else if (mode === 'reset' && onReset) {
        onReset(driver.id, pinCode);
        setMode('edit');
      }
    }
  };

  const handleToggle = () => {
    if (driver && onToggle) {
      onToggle(driver.id, driver.is_active || false);
    }
  };

  if (!driver) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-[#00BFA5]" />
            {mode === 'create' ? 'Configurar Acesso Mobile' : 'Gerenciar Acesso Mobile'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create' 
              ? 'Configure o PIN para acesso ao aplicativo mobile'
              : 'Gerencie o acesso mobile do motorista'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Driver Info */}
          <div className="p-4 bg-muted/50 rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">{driver.name}</p>
                <p className="text-sm text-muted-foreground">CPF: {driver.cpf ? formatDocument(driver.cpf) : '-'}</p>
              </div>
              {driver.has_access && (
                <Badge variant={driver.is_active ? 'default' : 'secondary'}>
                  {driver.is_active ? 'Ativo' : 'Inativo'}
                </Badge>
              )}
            </div>
            
            {driver.has_access && driver.last_login_at && (
              <div className="text-xs text-muted-foreground">
                Último acesso: {format(new Date(driver.last_login_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              </div>
            )}
          </div>

          {/* Edit Mode - Actions */}
          {mode === 'edit' && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-900">
                <Shield className="w-4 h-4 text-blue-600 mt-0.5" />
                <div className="flex-1 text-sm">
                  <p className="font-medium text-blue-900 dark:text-blue-100">
                    Acesso Mobile Configurado
                  </p>
                  <p className="text-blue-700 dark:text-blue-300 text-xs mt-1">
                    O motorista possui um PIN ativo para login no aplicativo. Para visualizar o PIN, você precisa redefini-lo.
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setMode('reset')}
                >
                  Redefinir PIN
                </Button>
                <Button
                  type="button"
                  variant={driver.is_active ? 'destructive' : 'default'}
                  className="flex-1"
                  onClick={handleToggle}
                  disabled={isLoading}
                >
                  {driver.is_active ? 'Desativar Acesso' : 'Ativar Acesso'}
                </Button>
              </div>
            </div>
          )}

          {/* Create/Reset Mode - PIN Form */}
          {(mode === 'create' || mode === 'reset') && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Security Warning */}
              <div className="flex items-start gap-2 p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-900">
                <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5" />
                <div className="flex-1 text-sm">
                  <p className="font-medium text-orange-900 dark:text-orange-100">
                    Importante: Anote o PIN
                  </p>
                  <p className="text-orange-700 dark:text-orange-300 text-xs mt-1">
                    Você precisará fornecer este PIN ao motorista para que ele possa fazer login no aplicativo
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="pin">
                  {mode === 'reset' ? 'Novo PIN' : 'PIN'} (4-6 dígitos)
                </Label>
                <Input
                  id="pin"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={pinCode}
                  onChange={(e) => setPinCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="Digite o PIN"
                  required
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  Use apenas números. Evite sequências como 1234 ou repetições como 1111
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPin">Confirmar PIN</Label>
                <Input
                  id="confirmPin"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="Digite o PIN novamente"
                  required
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (mode === 'reset') {
                      setMode('edit');
                      setPinCode('');
                      setConfirmPin('');
                    } else {
                      onOpenChange(false);
                    }
                  }}
                  disabled={isLoading}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={isLoading || !pinCode || pinCode !== confirmPin}>
                  {isLoading ? 'Salvando...' : mode === 'reset' ? 'Redefinir PIN' : 'Ativar Acesso'}
                </Button>
              </DialogFooter>
            </form>
          )}

          {/* Edit Mode - Close Button */}
          {mode === 'edit' && (
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Fechar
              </Button>
            </DialogFooter>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
